import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useCallback, type ReactNode } from "react";
import type { AuthChangeEvent } from "@supabase/supabase-js";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { initSentry, Sentry } from "@/lib/sentry";
import { applyStoredReferral } from "@/lib/referral";
import { applyStoredSignupSource, captureSignupSource } from "@/lib/signup-source";
import { DebugOverflow } from "@/components/DebugOverflow";

initSentry();


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "TurkeyDrive — афиша и сообщество русскоязычных в Турции" },
      {
        name: "description",
        content:
          "Находи события в своём городе, размещай свои мероприятия, вступай в сообщества по интересам и знакомься. Всё в одном приложении.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "TurkeyDrive" },
      { property: "og:locale", content: "ru_RU" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:title", content: "TurkeyDrive — афиша и сообщество русскоязычных в Турции" },
      { name: "twitter:title", content: "TurkeyDrive — афиша и сообщество русскоязычных в Турции" },
      { property: "og:description", content: "Находи события в своём городе, размещай свои мероприятия, вступай в сообщества по интересам и знакомься. Всё в одном приложении." },
      { name: "twitter:description", content: "Находи события в своём городе, размещай свои мероприятия, вступай в сообщества по интересам и знакомься. Всё в одном приложении." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/SlPHRDnqmuPUBfG94njA1Thk2tB3/social-images/social-1784016270578-A0B0406F-154E-489C-B73B-9C488FC9B153.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/SlPHRDnqmuPUBfG94njA1Thk2tB3/social-images/social-1784016270578-A0B0406F-154E-489C-B73B-9C488FC9B153.webp" },
      { name: "theme-color", content: "#0D5C58" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-title", content: "TurkeyDrive" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap",
      },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "apple-touch-icon", href: "/icons/apple-touch-icon.png" },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="ru">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  const handleAuthStateChange = useCallback(
    (event: AuthChangeEvent) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    },
    [router, queryClient],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider onAuthStateChange={handleAuthStateChange}>
        <ReferralSync />
        <Toaster position="top-center" />
        <DebugOverflow />
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Sentry.ErrorBoundary fallback={<ErrorComponent error={new Error("Render error")} reset={() => window.location.reload()} />}>
          <Outlet />
        </Sentry.ErrorBoundary>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function ReferralSync() {
  const { user } = useAuth();
  // Метки источника — на первой странице визита (включая /auth).
  useEffect(() => {
    captureSignupSource();
  }, []);
  useEffect(() => {
    if (user?.id) {
      applyStoredReferral(user.id).catch(() => {});
      applyStoredSignupSource(user.id).catch(() => {});
    }
  }, [user?.id]);
  return null;
}
